// Phase 3C / Iter83 — Market Snapshot Builder Tile.
//
// Operator-facing wrapper around the Phase 3B backend endpoint:
//   POST /api/forge/toolbox/market-snapshot/build
//
// Builds a transient, normalized, read-only market snapshot for a
// small batch of symbols and displays it as a per-symbol card grid.
// The operator can copy / download the snapshot JSON. Nothing is
// persisted — there is no localStorage, no DB write, no scanner
// auto-run, no Applicability Board fan-out, no module evaluator
// call from this tile.
//
// HARD CONSTRAINTS (asserted by Iter83 tests):
//  * Manual-run only. No HTTP on mount. No `useEffect` of any kind.
//  * Exactly one outbound POST per click, targeting only the
//    Phase 3B snapshot endpoint above.
//  * Never calls `/forge/candidates` (Wheel flagship), never calls
//    `/market-scanner/*`, never calls any scanner preview endpoint,
//    never calls broker / account / position / journal / admin /
//    trade / order endpoints.
//  * No ranking. No "winner" / "best" / "top" / "preferred" /
//    "recommend" / "auto-select" language outside disclaimers.
//  * No `localStorage`, no `IndexedDB`, no `sessionStorage`. The
//    snapshot lives only in React state for the lifetime of the
//    tile mount.

import React, { useState } from "react";
import axios from "axios";
import {
  ChevronDown, ChevronUp, Play, Eraser, Copy, Download,
  ShieldAlert, RefreshCw, AlertTriangle,
} from "lucide-react";
import { API } from "@/App";
import { useMarketSnapshot } from "@/contexts/MarketSnapshotContext";

const ENDPOINT = "/forge/toolbox/market-snapshot/build";

const POLICIES = Object.freeze([
  { id: "next_weekly",  label: "Next weekly",
    testid: "market-snapshot-builder-policy-next_weekly" },
  { id: "next_monthly", label: "Next monthly",
    testid: "market-snapshot-builder-policy-next_monthly" },
  { id: "exact",        label: "Exact (per symbol)",
    testid: "market-snapshot-builder-policy-exact" },
]);

const DEFAULT_SYMBOLS = "SPY";
const SNAPSHOT_EXPORT_VERSION = "phase3c_snapshot_export_v1";

function parseSymbols(text) {
  return Array.from(new Set(
    (text || "")
      .split(/[\s,;\n]+/)
      .map(s => s.trim().toUpperCase())
      .filter(Boolean)
  ));
}

function parseExplicit(text) {
  // Parse a `SYM=YYYY-MM-DD` per line block into a map. Lines that
  // do not match the shape are ignored — the backend will surface
  // MISSING_EXPLICIT_EXPIRATION row-level if needed.
  const out = {};
  (text || "").split(/\n+/).forEach(line => {
    const m = line.trim().match(/^([A-Z][A-Z0-9.\-]{0,9})\s*[:=]\s*(\d{4}-\d{2}-\d{2})\s*$/i);
    if (m) out[m[1].toUpperCase()] = m[2];
  });
  return out;
}

function relativeSeconds(iso) {
  if (!iso) return "—";
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return "—";
  const s = Math.max(0, Math.round((Date.now() - t) / 1000));
  return `${s}s ago`;
}

export default function MarketSnapshotBuilderTile() {
  const { setSnapshot: publishToContext } = useMarketSnapshot();
  const [open, setOpen] = useState(false);
  const [symbolsText, setSymbolsText] = useState(DEFAULT_SYMBOLS);
  const [policy, setPolicy] = useState("next_weekly");
  const [explicitText, setExplicitText] = useState("");
  const [includeIvr, setIncludeIvr] = useState(true);

  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);
  const [snapshot, setSnapshot] = useState(null);
  const [requestPayload, setRequestPayload] = useState(null);

  async function buildSnapshot() {
    setErrorMsg(null);
    const symbols = parseSymbols(symbolsText);
    if (symbols.length === 0) {
      setErrorMsg("Enter at least one symbol.");
      return;
    }
    if (symbols.length > 25) {
      setErrorMsg(`Hard cap is 25 symbols (got ${symbols.length}).`);
      return;
    }
    const payload = {
      symbols,
      expiration_policy: policy,
      include_ivr: includeIvr,
      max_symbols: 25,
    };
    if (policy === "exact") {
      payload.explicit_expirations = parseExplicit(explicitText);
    }
    setRequestPayload(payload);
    setLoading(true);
    try {
      const r = await axios.post(`${API}${ENDPOINT}`, payload);
      setSnapshot(r.data);
      // Iter84 — publish to in-memory MarketSnapshotContext so the
      // five toolbox module scanner panels can offer a Use Fresh
      // Snapshot source mode. NOT persisted.
      publishToContext(r.data, payload);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "string"
        ? detail
        : Array.isArray(detail)
          ? detail.map(d => d.msg || JSON.stringify(d)).join("; ")
          : (e?.message || "Network error");
      setErrorMsg(msg);
      setSnapshot(null);
    } finally {
      setLoading(false);
    }
  }

  function clearSnapshot() {
    setSnapshot(null);
    setErrorMsg(null);
    setRequestPayload(null);
    publishToContext(null, null);
  }

  async function copyJson() {
    const blob = buildExportEnvelope();
    try {
      await navigator.clipboard.writeText(JSON.stringify(blob, null, 2));
    } catch (_e) {
      // best-effort; do not call backend
    }
  }

  function downloadJson() {
    const blob = buildExportEnvelope();
    const fname = exportFilename(blob);
    const data = JSON.stringify(blob, null, 2);
    const url = URL.createObjectURL(
      new Blob([data], { type: "application/json" })
    );
    const a = document.createElement("a");
    a.href = url;
    a.download = fname;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  function buildExportEnvelope() {
    return {
      snapshot_export_version: SNAPSHOT_EXPORT_VERSION,
      exported_at: new Date().toISOString(),
      source_panel: "MarketSnapshotBuilderTile",
      endpoint: `/api${ENDPOINT}`,
      request_payload_used: requestPayload,
      response_snapshot: snapshot,
      read_only: true,
      execution_enabled: false,
      journal_mutation_enabled: false,
      broker_execution_enabled: false,
      disclaimer:
        "Read-only market snapshot. No trade execution. No strategy " +
        "ranking. No winner. No journal write. No persistence. No " +
        "broker or account fetch. Manual fresh snapshot only.",
    };
  }

  function exportFilename(blob) {
    const syms = (blob.request_payload_used?.symbols || []).slice(0, 4).join("-")
      || "batch";
    const ts = new Date().toISOString().replace(/[:.]/g, "-");
    return `arxmentis_market_snapshot_${syms}_${ts}.json`;
  }

  return (
    <div
      data-testid="market-snapshot-builder-tile"
      className="mb-5 rounded-sm border border-[#26262B] bg-[#0A0A0E]"
    >
      <button
        type="button"
        data-testid="market-snapshot-builder-toggle"
        onClick={() => setOpen(v => !v)}
        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-[#121216]"
      >
        <RefreshCw size={16} className="text-[#E0E0E0]" />
        <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#E0E0E0]">
          Market Snapshot Builder
        </span>
        <span className="text-[9px] font-mono uppercase tracking-[0.12em] text-[#5F5F66]">
          · phase 3c · manual fresh snapshot · no scanner run · no execution
        </span>
        <span className="ml-auto text-[#5F5F66]">
          {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </span>
      </button>

      {open && (
        <div className="border-t border-[#26262B]">
          {/* Safety banner */}
          <div
            data-testid="market-snapshot-builder-banner"
            className="px-4 py-2 border-b border-[#26262B] bg-[#FFB800]/5 text-[#FFB800] text-[10px] font-mono flex items-center gap-3 flex-wrap"
          >
            <ShieldAlert size={12} />
            <span data-testid="market-snapshot-builder-banner-manual">
              Manual fresh snapshot only.
            </span>
            <span className="text-[#FFB800]/40">·</span>
            <span data-testid="market-snapshot-builder-banner-no-scanner-run">
              No scanner run.
            </span>
            <span className="text-[#FFB800]/40">·</span>
            <span data-testid="market-snapshot-builder-banner-no-ranking">
              No strategy ranking.
            </span>
            <span className="text-[#FFB800]/40">·</span>
            <span data-testid="market-snapshot-builder-banner-no-execution">
              No trade execution.
            </span>
            <span className="text-[#FFB800]/40">·</span>
            <span data-testid="market-snapshot-builder-banner-no-journal">
              No journal write.
            </span>
            <span className="text-[#FFB800]/40">·</span>
            <span data-testid="market-snapshot-builder-banner-no-persistence">
              No persistence.
            </span>
            <span className="text-[#FFB800]/40">·</span>
            <span data-testid="market-snapshot-builder-banner-no-account-fetch">
              No account / position fetch.
            </span>
          </div>

          {/* Inputs */}
          <div className="px-4 py-3 border-b border-[#26262B] space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <label className="block">
                <span className="text-[10px] font-mono uppercase tracking-[0.14em] text-[#8A8A93]">
                  Symbols (comma / space / newline separated · max 25)
                </span>
                <textarea
                  data-testid="market-snapshot-builder-symbols"
                  value={symbolsText}
                  onChange={e => setSymbolsText(e.target.value)}
                  rows={2}
                  spellCheck={false}
                  className="w-full mt-1 bg-[#08080A] border border-[#26262B] rounded-sm px-2 py-1 text-[11px] font-mono text-[#E0E0E0] focus:outline-none"
                />
              </label>
              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <span className="text-[10px] font-mono uppercase tracking-[0.14em] text-[#8A8A93]">
                    Expiration policy
                  </span>
                  <select
                    data-testid="market-snapshot-builder-policy"
                    value={policy}
                    onChange={e => setPolicy(e.target.value)}
                    className="w-full mt-1 bg-[#08080A] border border-[#26262B] rounded-sm px-2 py-1 text-[11px] font-mono text-[#E0E0E0] focus:outline-none"
                  >
                    {POLICIES.map(p => (
                      <option
                        key={p.id} value={p.id}
                        data-testid={p.testid}
                      >
                        {p.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="block">
                  <span className="text-[10px] font-mono uppercase tracking-[0.14em] text-[#8A8A93]">
                    Include IVR
                  </span>
                  <div className="mt-1 flex items-center gap-2">
                    <input
                      type="checkbox"
                      data-testid="market-snapshot-builder-include-ivr"
                      checked={includeIvr}
                      onChange={e => setIncludeIvr(e.target.checked)}
                      className="accent-[#E0E0E0]"
                    />
                    <span className="text-[10px] font-mono text-[#8A8A93]">
                      Pull Tastytrade IVR batch (read-only).
                    </span>
                  </div>
                </label>
              </div>
            </div>

            {policy === "exact" && (
              <label className="block">
                <span className="text-[10px] font-mono uppercase tracking-[0.14em] text-[#8A8A93]">
                  Explicit expirations (one per line: SYM=YYYY-MM-DD)
                </span>
                <textarea
                  data-testid="market-snapshot-builder-explicit-expirations"
                  value={explicitText}
                  onChange={e => setExplicitText(e.target.value)}
                  rows={3}
                  spellCheck={false}
                  placeholder="SPY=2026-06-21&#10;AAPL=2026-06-21"
                  className="w-full mt-1 bg-[#08080A] border border-[#26262B] rounded-sm px-2 py-1 text-[11px] font-mono text-[#E0E0E0] focus:outline-none"
                />
              </label>
            )}

            <div className="flex items-center gap-2 flex-wrap">
              <button
                type="button"
                data-testid="market-snapshot-builder-run"
                onClick={buildSnapshot}
                disabled={loading}
                className="px-3 py-1 rounded-sm border border-[#E0E0E0]/30 bg-[#E0E0E0]/10 text-[10px] font-mono uppercase tracking-[0.14em] text-[#E0E0E0] hover:bg-[#E0E0E0]/20 inline-flex items-center gap-1 disabled:opacity-50"
              >
                <Play size={10} />
                {loading ? "Building snapshot…" : "Build Fresh Market Snapshot"}
              </button>
              <button
                type="button"
                data-testid="market-snapshot-builder-clear"
                onClick={clearSnapshot}
                className="px-2 py-1 rounded-sm border border-[#26262B] text-[9px] font-mono uppercase tracking-[0.12em] text-[#8A8A93] hover:text-[#E0E0E0] hover:border-[#3F3F47] inline-flex items-center gap-1"
              >
                <Eraser size={10} /> Clear snapshot
              </button>
              {snapshot && (
                <>
                  <button
                    type="button"
                    data-testid="market-snapshot-builder-copy"
                    onClick={copyJson}
                    className="px-2 py-1 rounded-sm border border-[#26262B] text-[9px] font-mono uppercase tracking-[0.12em] text-[#8A8A93] hover:text-[#E0E0E0] hover:border-[#3F3F47] inline-flex items-center gap-1"
                  >
                    <Copy size={10} /> Copy snapshot JSON
                  </button>
                  <button
                    type="button"
                    data-testid="market-snapshot-builder-download"
                    onClick={downloadJson}
                    className="px-2 py-1 rounded-sm border border-[#26262B] text-[9px] font-mono uppercase tracking-[0.12em] text-[#8A8A93] hover:text-[#E0E0E0] hover:border-[#3F3F47] inline-flex items-center gap-1"
                  >
                    <Download size={10} /> Download snapshot JSON
                  </button>
                </>
              )}
            </div>

            {errorMsg && (
              <div
                data-testid="market-snapshot-builder-error"
                className="flex items-start gap-2 text-[10px] font-mono text-[#FF3366] border border-[#FF3366]/30 bg-[#FF3366]/5 px-2 py-1.5 rounded-sm"
              >
                <AlertTriangle size={11} className="shrink-0 mt-0.5" />
                <span>{errorMsg}</span>
              </div>
            )}
          </div>

          {/* Output */}
          {!snapshot && !loading && !errorMsg && (
            <div
              data-testid="market-snapshot-builder-empty"
              className="px-4 py-3 text-[10px] font-mono text-[#5F5F66]"
            >
              No snapshot yet. Enter symbols and click Build Fresh Market Snapshot.
            </div>
          )}

          {snapshot && (
            <SnapshotResult
              snapshot={snapshot}
              requestPayload={requestPayload}
            />
          )}
        </div>
      )}
    </div>
  );
}

function SnapshotResult({ snapshot, requestPayload }) {
  const rows = snapshot.rows || [];
  return (
    <div
      data-testid="market-snapshot-builder-result"
      className="px-4 py-3 space-y-3"
    >
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[10px] font-mono">
        <span
          data-testid="market-snapshot-builder-result-snapshot-id"
          className="text-[#5F5F66]"
        >
          snapshot_id
          <span className="text-[#E0E0E0] ml-2">{snapshot.snapshot_id}</span>
        </span>
        <span
          data-testid="market-snapshot-builder-result-snapshot-version"
          className="text-[#5F5F66]"
        >
          version
          <span className="text-[#E0E0E0] ml-2">{snapshot.snapshot_version}</span>
        </span>
        <span
          data-testid="market-snapshot-builder-result-as-of"
          className="text-[#5F5F66]"
        >
          as_of
          <span className="text-[#E0E0E0] ml-2">{snapshot.as_of}</span>
          <span className="text-[#5F5F66] ml-2">({relativeSeconds(snapshot.as_of)})</span>
        </span>
        <Flag label="read_only"               value={snapshot.read_only} testid="market-snapshot-builder-result-flag-read-only" />
        <Flag label="execution_enabled"       value={snapshot.execution_enabled} testid="market-snapshot-builder-result-flag-execution-enabled" />
        <Flag label="journal_mutation_enabled" value={snapshot.journal_mutation_enabled} testid="market-snapshot-builder-result-flag-journal-mutation-enabled" />
        <Flag label="broker_execution_enabled" value={snapshot.broker_execution_enabled} testid="market-snapshot-builder-result-flag-broker-execution-enabled" />
        {snapshot.rate_limited && (
          <span
            data-testid="market-snapshot-builder-result-rate-limited"
            className="px-1.5 py-0.5 rounded-sm border border-[#FFB800]/40 bg-[#FFB800]/5 text-[#FFB800]"
          >
            RATE_LIMITED
          </span>
        )}
        {snapshot.partial && (
          <span
            data-testid="market-snapshot-builder-result-partial"
            className="px-1.5 py-0.5 rounded-sm border border-[#FFB800]/40 bg-[#FFB800]/5 text-[#FFB800]"
          >
            PARTIAL
          </span>
        )}
      </div>

      <div
        data-testid="market-snapshot-builder-provenance-chip"
        className="text-[9px] font-mono uppercase tracking-[0.12em] text-[#00FF88]/80 border border-[#00FF88]/20 bg-[#00FF88]/5 px-2 py-1 rounded-sm inline-block"
      >
        Using snapshot {snapshot.snapshot_id} · fresh {relativeSeconds(snapshot.as_of)}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {rows.map(row => (
          <SnapshotRowCard key={row.symbol} row={row} />
        ))}
      </div>

      <details
        data-testid="market-snapshot-builder-full-json"
        className="border border-[#26262B] rounded-sm"
      >
        <summary className="cursor-pointer px-2 py-1 text-[10px] font-mono uppercase tracking-[0.14em] text-[#8A8A93] hover:text-[#E0E0E0]">
          Full snapshot JSON (raw response)
        </summary>
        <pre className="mt-1 text-[10px] font-mono text-[#E0E0E0] whitespace-pre-wrap break-words bg-[#08080A] p-2 max-h-96 overflow-auto">
          {JSON.stringify(snapshot, null, 2)}
        </pre>
      </details>

      <details
        data-testid="market-snapshot-builder-request-json"
        className="border border-[#26262B] rounded-sm"
      >
        <summary className="cursor-pointer px-2 py-1 text-[10px] font-mono uppercase tracking-[0.14em] text-[#8A8A93] hover:text-[#E0E0E0]">
          Request payload used
        </summary>
        <pre className="mt-1 text-[10px] font-mono text-[#E0E0E0] whitespace-pre-wrap break-words bg-[#08080A] p-2 max-h-48 overflow-auto">
          {JSON.stringify(requestPayload, null, 2)}
        </pre>
      </details>
    </div>
  );
}

function Flag({ label, value, testid }) {
  const ok = value === true;
  const bad = value === true && (label !== "read_only");
  // read_only=true is good (green). execution/journal/broker=true would be bad (red),
  // but with the Phase 3B contract they are always false.
  const cls = label === "read_only"
    ? (ok ? "border-[#00FF88]/40 bg-[#00FF88]/5 text-[#00FF88]"
          : "border-[#FF3366]/40 bg-[#FF3366]/5 text-[#FF3366]")
    : (ok ? "border-[#FF3366]/40 bg-[#FF3366]/5 text-[#FF3366]"
          : "border-[#26262B] text-[#8A8A93]");
  return (
    <span
      data-testid={testid}
      className={`px-1.5 py-0.5 rounded-sm border text-[9px] uppercase tracking-[0.12em] ${cls}`}
    >
      {label}: {String(value)}
    </span>
  );
}

function SnapshotRowCard({ row }) {
  const sym = row.symbol;
  const chain = row.chain || [];
  const puts = chain.filter(r => r.type === "put").length;
  const calls = chain.filter(r => r.type === "call").length;
  return (
    <div
      data-testid={`market-snapshot-builder-row-${sym}`}
      className="rounded-sm border border-[#26262B] bg-[#08080A] p-3 text-[10px] font-mono space-y-1"
    >
      <div className="flex items-center gap-2 flex-wrap">
        <span
          data-testid={`market-snapshot-builder-row-${sym}-symbol`}
          className="text-[13px] font-bold uppercase tracking-[0.14em] text-[#E0E0E0]"
        >
          {sym}
        </span>
        <span
          data-testid={`market-snapshot-builder-row-${sym}-source`}
          className="text-[#8A8A93] uppercase tracking-[0.12em]"
        >
          {row.source}
        </span>
        {row.as_of && (
          <span
            data-testid={`market-snapshot-builder-row-${sym}-as-of`}
            className="text-[#5F5F66]"
          >
            {relativeSeconds(row.as_of)}
          </span>
        )}
      </div>

      <div className="flex items-center gap-3 flex-wrap text-[#8A8A93]">
        {row.spot_price != null && (
          <span data-testid={`market-snapshot-builder-row-${sym}-spot`}>
            SPOT <span className="text-[#E0E0E0] font-bold">{row.spot_price}</span>
            {row.spot_source && (
              <span className="ml-1 text-[#5F5F66]">({row.spot_source})</span>
            )}
          </span>
        )}
        {row.chosen_expiration && (
          <span data-testid={`market-snapshot-builder-row-${sym}-expiration`}>
            EXP <span className="text-[#E0E0E0] font-bold">{row.chosen_expiration}</span>
          </span>
        )}
        {row.dte != null && (
          <span data-testid={`market-snapshot-builder-row-${sym}-dte`}>
            DTE <span className="text-[#E0E0E0] font-bold">{row.dte}</span>
          </span>
        )}
        {row.ivr != null && (
          <span data-testid={`market-snapshot-builder-row-${sym}-ivr`}>
            IVR <span className="text-[#E0E0E0] font-bold">{row.ivr}</span>
          </span>
        )}
        {row.atm_iv != null && (
          <span data-testid={`market-snapshot-builder-row-${sym}-atm-iv`}>
            ATM_IV <span className="text-[#E0E0E0] font-bold">{row.atm_iv}</span>
          </span>
        )}
      </div>

      <div className="flex items-center gap-3 flex-wrap text-[#8A8A93]">
        <span data-testid={`market-snapshot-builder-row-${sym}-chain-count`}>
          CHAIN <span className="text-[#E0E0E0] font-bold">{chain.length}</span>
        </span>
        <span data-testid={`market-snapshot-builder-row-${sym}-puts`}>
          PUTS <span className="text-[#E0E0E0] font-bold">{puts}</span>
        </span>
        <span data-testid={`market-snapshot-builder-row-${sym}-calls`}>
          CALLS <span className="text-[#E0E0E0] font-bold">{calls}</span>
        </span>
      </div>

      {Array.isArray(row.warnings) && row.warnings.length > 0 && (
        <div
          data-testid={`market-snapshot-builder-row-${sym}-warnings`}
          className="flex items-center gap-1 flex-wrap"
        >
          {row.warnings.map((w, i) => (
            <span
              key={i}
              className="px-1.5 py-0.5 rounded-sm border border-[#FFB800]/40 bg-[#FFB800]/5 text-[#FFB800] text-[9px] uppercase tracking-[0.12em]"
            >
              {w}
            </span>
          ))}
        </div>
      )}

      {Array.isArray(row.errors) && row.errors.length > 0 && (
        <div
          data-testid={`market-snapshot-builder-row-${sym}-errors`}
          className="flex items-start gap-1 flex-col"
        >
          {row.errors.map((e, i) => (
            <span
              key={i}
              className="text-[#FF3366] text-[9px] break-words"
            >
              {e}
            </span>
          ))}
        </div>
      )}

      <details
        data-testid={`market-snapshot-builder-row-${sym}-chain-preview`}
        className="mt-1"
      >
        <summary className="cursor-pointer text-[9px] uppercase tracking-[0.14em] text-[#5F5F66] hover:text-[#E0E0E0]">
          Normalized chain preview (first 10 rows)
        </summary>
        <pre className="mt-1 text-[10px] font-mono text-[#E0E0E0] whitespace-pre-wrap break-words bg-[#0A0A0E] p-2 max-h-48 overflow-auto">
          {JSON.stringify(chain.slice(0, 10), null, 2)}
        </pre>
      </details>
    </div>
  );
}
